import { useEffect, useState } from 'react';

const DEFAULT_DIR_THRESHOLD = 5;

/**
 * While `enabled` is true, shows when the user scrolls up and hides when they scroll down.
 * Optional `minScrollY` keeps the control hidden until the page is scrolled down that far (e.g. PLP).
 */
export function useRevealOnScrollUpWhile(
  enabled: boolean,
  options?: { minScrollY?: number; directionThreshold?: number }
): boolean {
  const { minScrollY = 0, directionThreshold = DEFAULT_DIR_THRESHOLD } = options ?? {};
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset when disabled
      setOpen(false);
      return;
    }

    let lastY = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      if (y < minScrollY) {
        setOpen(false);
        lastY = y;
        return;
      }
      const d = y - lastY;
      lastY = y;
      if (Math.abs(d) < directionThreshold) return;
      if (d < 0) setOpen(true);
      else if (d > 0) setOpen(false);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled, minScrollY, directionThreshold]);

  return open;
}
